import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, X } from "lucide-react";
import api from "../lib/api";

// Booking-style floating "recently booked" toasts.
// Cycles through recent anonymized approved reservations.

const timeAgo = (iso) => {
    if (!iso) return "";
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const min = Math.max(1, Math.floor((now - then) / 60000));
        if (min < 60) return `${min} dk önce`;
        const h = Math.floor(min / 60);
        if (h < 24) return `${h} saat önce`;
        const d = Math.floor(h / 24);
        return `${d} gün önce`;
    } catch { return ""; }
};

const RecentBookings = () => {
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/hotels/recent-bookings?hours=720")
            .then(({ data }) => { if (alive) setItems(Array.isArray(data) ? data : []); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    // Rotate every 8 seconds, first popup after a 4-second delay
    useEffect(() => {
        if (items.length === 0 || dismissed) return undefined;
        let mounted = true;
        const start = setTimeout(() => { if (mounted) setVisible(true); }, 4000);
        const rotate = setInterval(() => {
            if (!mounted) return;
            setVisible(false);
            setTimeout(() => {
                if (!mounted) return;
                setIdx((i) => (i + 1) % items.length);
                setVisible(true);
            }, 600);
        }, 8000);
        return () => { mounted = false; clearTimeout(start); clearInterval(rotate); };
    }, [items.length, dismissed]);

    if (items.length === 0 || dismissed) return null;
    const current = items[idx];

    return (
        <AnimatePresence>
            {visible && current && (
                <motion.div
                    initial={{ opacity: 0, y: 24, x: -12 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    exit={{ opacity: 0, y: 24, x: -12 }}
                    transition={{ duration: 0.35 }}
                    className="fixed bottom-6 left-4 lg:left-8 z-40 max-w-xs bg-emerald_surface border border-gold/50 shadow-2xl"
                    data-testid="recent-booking-toast"
                >
                    <div className="flex items-start gap-3 p-3 pr-8">
                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-gold" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs text-bone leading-tight">
                                <span className="font-semibold text-gold">{current.masked_name}</span> rezervasyon yaptı
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-muted_green mt-1">{timeAgo(current.when)}</div>
                        </div>
                        <button
                            onClick={() => setDismissed(true)}
                            className="absolute top-1.5 right-1.5 text-muted_green hover:text-bone"
                            aria-label="Kapat"
                            data-testid="dismiss-recent-toast"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default RecentBookings;
